import { Loader } from '@googlemaps/js-api-loader'
import type {
  GoogleMapProps as BaseGoogleMapProps,
  MarkerProps,
} from '@react-google-maps/api'
import { GoogleMap as GMap, Marker } from '@react-google-maps/api'
import { useLocale } from '@ur-solutions/react-hooks'
import merge from 'lodash/merge'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { GOOGLE_API_KEY } from '@/utils/env'

export interface LatLng {
  lat: number
  lng: number
}

export interface GoogleMapRef {
  map: google.maps.Map | null
}

const googleLoader = new Loader({
  apiKey: GOOGLE_API_KEY,
  libraries: ['places'],
})

interface GoogleMapProps {
  center?: LatLng
  zoom?: number

  options?: google.maps.MapOptions
  mapContainerStyle?: React.CSSProperties

  markers?: MarkerProps[]

  onLoad?: BaseGoogleMapProps['onLoad']
  onClick?: (event: google.maps.MapMouseEvent) => void
  onZoomChanged?: (zoom: number) => void
}

export const GoogleMap = React.forwardRef<GoogleMapRef, GoogleMapProps>(
  (
    {
      center = {
        lat: 63.2574549,
        lng: 11.1844929,
      },
      zoom = 7.75,

      options = {},
      mapContainerStyle = {},

      markers = [],

      onLoad,
      onClick,
      onZoomChanged,
    },
    ref
  ) => {
    const { locale } = useLocale()

    const [scriptLoaded, setScriptLoaded] = useState(false)
    const [map, setMap] = useState<google.maps.Map | null>(null)

    useImperativeHandle(ref, () => ({
      map,
    }))

    useEffect(() => {
      // @ts-expect-error language is readonly
      googleLoader.language = locale
      void googleLoader.load().then(() => setScriptLoaded(true))

      // the loader cannot be reloaded with new options no matter what. Only a full reload of the
      // page will be allowed by the google maps library to instanciate a new language option.
      // eslint-disable-next-line
    }, [])

    const handleLoad = useCallback(
      (map: google.maps.Map) => {
        setMap(map)
        void onLoad?.(map)
      },
      [onLoad]
    )

    const handleUnmount = useCallback(() => {
      setMap(null)
    }, [])

    const handleZoomChanged = useCallback(() => {
      onZoomChanged?.(map?.getZoom() ?? 8)
    }, [map, onZoomChanged])

    const style = merge(
      {
        width: '100%',
        height: '530px',
      },
      mapContainerStyle
    )

    if (!scriptLoaded) return null

    return (
      <GMap
        center={center}
        zoom={zoom}
        mapContainerStyle={style}
        options={options}
        onLoad={handleLoad}
        onUnmount={handleUnmount}
        onClick={onClick}
        onZoomChanged={handleZoomChanged}
      >
        {markers.map((marker, index) => (
          <Marker key={index} {...marker} />
        ))}
      </GMap>
    )
  }
)
